import { useEffect, useReducer } from "react";
import { getNoBackgroundLogos } from "@/controllers/stock";

// Security ids whose logos are legible on dark backgrounds and must not get the
// grey dark-mode backing. Shared across components via module memory and persisted
// in localStorage so the endpoint is only hit when the cache is missing or older
// than MAX_AGE_MS (replaces the bundled noBackgroundStocks.json — see ortex/web#1666).
let noBackgroundLogos: Set<number> | null = null;
let isFresh = false;
let fetchPromise: Promise<void> | null = null;

const STORAGE_KEY = "noBackgroundLogos.v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const readCache = (): { ids: number[]; fresh: boolean } | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed?.ids) || typeof parsed?.storedAt !== "number") return null;
        return { ids: parsed.ids, fresh: Date.now() - parsed.storedAt < MAX_AGE_MS };
    } catch {
        return null;
    }
};

// The list is ~27k ids: storing it waits for an idle moment, so it never lengthens the page's
// start-up (the module copy serves this page load meanwhile).
const writeCache = (ids: number[]) => {
    const write = () => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ storedAt: Date.now(), ids }));
        } catch {
            // Quota exceeded / private mode — module memory still works for this page load.
        }
    };
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(write, { timeout: 10000 });
    else setTimeout(write, 2000);
};

// Returns a promise to await when a fetch is in flight, or null when the set is
// already usable and fresh. A stale cache is served immediately and revalidated
// in the background; a failed fetch keeps serving the stale copy.
const ensureLoaded = (): Promise<void> | null => {
    if (typeof window === "undefined" || isFresh) return null;
    if (!noBackgroundLogos) {
        const cached = readCache();
        if (cached) {
            noBackgroundLogos = new Set(cached.ids);
            isFresh = cached.fresh;
            if (isFresh) return null;
        }
    }
    if (!fetchPromise) {
        fetchPromise = getNoBackgroundLogos()
            .then((ids) => {
                noBackgroundLogos = new Set(ids);
                isFresh = true;
                writeCache(ids);
            })
            .catch(() => {
                fetchPromise = null;
            });
    }
    return fetchPromise;
};

export const useNoBackgroundLogos = () => {
    const [, forceUpdate] = useReducer((n: number) => n + 1, 0);

    useEffect(() => {
        const hadSet = !!noBackgroundLogos;
        const pending = ensureLoaded();
        if (!pending || hadSet) return;
        let mounted = true;
        pending.then(() => mounted && forceUpdate());
        return () => {
            mounted = false;
        };
    }, []);

    const logoNoBackground = (securityId?: number | null) => !!securityId && !!noBackgroundLogos?.has(securityId);

    return { logoNoBackground };
};
